import type { ErrorComponentProps } from '@tanstack/react-router';
import { useEffect } from 'react';

import { ApiError } from '@/lib/api/error';
import { ForbiddenApiError } from '@/lib/api/error.forbidden';
import { InternalServerApiError } from '@/lib/api/error.internal-server';
import { NoOrganizationAccessError } from '@/lib/api/error.no-organization-access';
import { PersonalEmailLoginDeniedError } from '@/lib/api/error.personal-email-login-denied';
import { UnauthorizedApiError } from '@/lib/api/error.unauthorized';
import { getLogger } from '@/lib/logger';
import { handleSystemApiError } from '@/lib/queryClient';

import { closeGlobalErrorDialog } from './GlobalErrorDialog/actions';
import { useGlobalErrorDialog } from './GlobalErrorDialog/state';
import { ServerError } from './ServerError';

/**
 * ForbiddenApiError / UnauthorizedApiError / 4xx ApiError 以外のエラーをサーバーエラーとみなす。
 * ZodError やセッション取得失敗などの想定外エラーもここに含まれる。
 */
function isServerError(error: Error): boolean {
  if (error instanceof ForbiddenApiError) return false;
  if (error instanceof UnauthorizedApiError) return false;
  // 組織未所属は /me の案内画面へ誘導するため、サーバーエラー画面には倒さない。
  if (error instanceof NoOrganizationAccessError) return false;
  // 再ログインでのみ復旧可能なため、サーバーエラー画面ではなく再ログイン導線へ回す。
  if (error instanceof PersonalEmailLoginDeniedError) return false;
  if (error instanceof InternalServerApiError) return true;
  if (error instanceof ApiError) {
    return error.getResponse().statusCode >= 500;
  }
  return true;
}

export function ErrorComponent({ error }: ErrorComponentProps) {
  getLogger().error('エラーが発生しました。', error);

  const serverError = isServerError(error);

  useEffect(() => {
    if (serverError) {
      closeGlobalErrorDialog();
      // QueryCache.onError のイベントリスナーが動的インポートで非同期にダイアログを開く場合があるため、
      // subscribe で監視し、開かれたら即座に閉じる
      const unsubscribe = useGlobalErrorDialog.subscribe((state) => {
        if (state.isOpen) {
          closeGlobalErrorDialog();
        }
      });
      return unsubscribe;
    }
    /**
     * NOTE: Component / API で発生したエラーの場合も含めてハンドリングしている
     * → 特に useSuspenseQuery でエラーが発生した場合は tanstack の router に渡ってくる
     * → （<Suspense > 等でエラーを吸収されてしまうため）
     * → つまり、QueryClient で適用している onError は適用されない
     */
    handleSystemApiError({ error, isHistoryBackIfPossible: true });
    return () => closeGlobalErrorDialog();
  }, [error, serverError]);

  if (serverError) {
    return <ServerError />;
  }

  return null;
}
